import { useLayoutEffect, useRef, useState, type HTMLAttributes } from 'react';
import { useReducedMotion } from './useReducedMotion';
import './AnimatedPresence.css';

/** Mount and retire a region without cutting off its exit or replacing live controls. */
export function AnimatedPresence({ show, children, keepMounted = false, className = '', ...props }: HTMLAttributes<HTMLDivElement> & { show: boolean; keepMounted?: boolean }) {
  const reduced = useReducedMotion();
  const [present, setPresent] = useState(show);
  const [settled, setSettled] = useState(show && reduced);
  const retained = useRef(children);
  if (show) retained.current = children;
  useLayoutEffect(() => {
    setSettled(show && reduced);
    if (show) {
      setPresent(true);
      if (reduced) return undefined;
      const timer = setTimeout(() => setSettled(true), 280);
      return () => clearTimeout(timer);
    }
    if (reduced) { setPresent(false); return undefined; }
    const timer = setTimeout(() => setPresent(false), 280);
    return () => clearTimeout(timer);
  }, [show, reduced]);
  if (!show && !present && !keepMounted) return null;
  return <div {...props} hidden={!show && !present} className={`animated-presence ${className}`} data-open={show} data-settled={show && (reduced || settled)} aria-hidden={!show} inert={!show}>
    <div className="animated-presence-clip">{show || keepMounted ? children : retained.current}</div>
  </div>;
}
